import React, { useState, useEffect, useCallback } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { toast } from 'sonner';
import axios from 'axios';
import {
    ArrowLeft, Save, Bot, Cpu, MessageSquare, Brain,
    Shield, Eye, EyeOff, Plus, Trash2, RefreshCw, X, Github
} from 'lucide-react';

const API_BASE = process.env.REACT_APP_BACKEND_URL;

const api = axios.create({ baseURL: `${API_BASE}/api/superadmin/agents` });
api.interceptors.request.use((config) => {
    const token = localStorage.getItem('superadmin_token');
    if (token) config.headers.Authorization = `Bearer ${token}`;
    return config;
});

const statusColors = {
    idle: 'bg-green-100 text-green-800',
    busy: 'bg-blue-100 text-blue-800',
    paused: 'bg-yellow-100 text-yellow-800',
    offline: 'bg-gray-100 text-gray-800',
    blocked: 'bg-red-100 text-red-800',
    online: 'bg-emerald-100 text-emerald-800',
};

const TABS = [
    { key: 'general', label: 'General', icon: Bot },
    { key: 'persona', label: 'Persona', icon: MessageSquare },
    { key: 'llm', label: 'LLM', icon: Cpu },
    { key: 'skills', label: 'Skills', icon: Brain },
    { key: 'memory', label: 'Memory', icon: Eye },
    { key: 'vars', label: 'Vars', icon: Shield },
];

// ─── Markdown Preview ───────────────────────────────────────────
function MarkdownPreview({ content }) {
    if (!content) return <span className="text-gray-400 italic">No content</span>;
    // Simple markdown-to-HTML: headings, bold, code, lists
    const html = content
        .replace(/&/g, '&amp;').replace(/</g, '&lt;').replace(/>/g, '&gt;')
        .replace(/^### (.+)$/gm, '<h3 class="text-sm font-bold mt-2">$1</h3>')
        .replace(/^## (.+)$/gm, '<h2 class="text-base font-bold mt-3">$1</h2>')
        .replace(/^# (.+)$/gm, '<h1 class="text-lg font-bold mt-3">$1</h1>')
        .replace(/\*\*(.+?)\*\*/g, '<strong>$1</strong>')
        .replace(/`(.+?)`/g, '<code class="bg-gray-100 px-1 rounded text-xs">$1</code>')
        .replace(/^- (.+)$/gm, '<li class="ml-4">$1</li>')
        .replace(/\n/g, '<br/>');
    return <div className="prose prose-sm max-w-none" dangerouslySetInnerHTML={{ __html: html }} />;
}

// ─── General Tab ─────────────────────────────────────────────────
function GeneralTab({ form, setForm, workspaces }) {
    const statusOptions = ['idle', 'busy', 'paused', 'offline', 'blocked', 'online'];
    const toggleWorkspace = (wsId) => {
        setForm((prev) => {
            const has = prev.workspace_ids.includes(wsId);
            return { ...prev, workspace_ids: has ? prev.workspace_ids.filter((id) => id !== wsId) : [...prev.workspace_ids, wsId] };
        });
    };
    return (
        <div className="space-y-5">
            <div>
                <label className="block text-sm font-medium mb-1">Agent Name</label>
                <input type="text" value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })}
                    className="w-full border rounded-lg px-3 py-2" placeholder="PM Agent" />
            </div>
            <div className="grid grid-cols-2 gap-4">
                <div>
                    <label className="block text-sm font-medium mb-1">Status</label>
                    <select value={form.status} onChange={(e) => setForm({ ...form, status: e.target.value })}
                        className="w-full border rounded-lg px-3 py-2">
                        {statusOptions.map((s) => <option key={s} value={s}>{s}</option>)}
                    </select>
                </div>
                <div>
                    <label className="block text-sm font-medium mb-1">Max Concurrent Tasks</label>
                    <input type="number" min={1} max={20} value={form.max_concurrent_tasks || 3}
                        onChange={(e) => setForm({ ...form, max_concurrent_tasks: parseInt(e.target.value) || 1 })}
                        className="w-full border rounded-lg px-3 py-2" />
                </div>
            </div>
            <div>
                <label className="block text-sm font-medium mb-1">Endpoint URL <span className="text-gray-400">(read-only)</span></label>
                <input type="text" value={form.endpoint_url || ''} readOnly
                    className="w-full border rounded-lg px-3 py-2 bg-gray-50 text-gray-500" />
            </div>
            <div>
                <label className="block text-sm font-medium mb-2">Workspaces</label>
                <div className="max-h-48 overflow-y-auto border rounded-lg p-2">
                    {workspaces.map((ws) => (
                        <label key={ws.id} className="flex items-center gap-2 py-1 hover:bg-gray-50 rounded px-1">
                            <input type="checkbox" checked={(form.workspace_ids || []).includes(ws.id)} onChange={() => toggleWorkspace(ws.id)} />
                            <span className="text-sm">{ws.name}</span>
                        </label>
                    ))}
                    {workspaces.length === 0 && <div className="text-sm text-gray-400">No workspaces found.</div>}
                </div>
            </div>
        </div>
    );
}

// ─── Persona Tab ──────────────────────────────────────────────────
function PersonaTab({ form, setForm }) {
    const fields = [
        { key: 'persona', label: 'Persona', placeholder: '# Persona\nYou are an expert PM AI...' },
        { key: 'jobdesk', label: 'Jobdesk (Responsibilities)', placeholder: '## Responsibilities\n- Review pull requests...' },
        { key: 'instructions', label: 'Instructions', placeholder: '## Instructions\nAlways respond in Bahasa Indonesia...' },
    ];
    const [preview, setPreview] = useState({});
    return (
        <div className="space-y-6">
            {fields.map(({ key, label, placeholder }) => (
                <div key={key}>
                    <div className="flex items-center justify-between mb-1">
                        <label className="text-sm font-medium">{label}</label>
                        <button onClick={() => setPreview((p) => ({ ...p, [key]: !p[key] }))}
                            className="text-xs text-blue-600 hover:underline flex items-center gap-1">
                            {preview[key] ? <><EyeOff className="w-3 h-3" /> Edit</> : <><Eye className="w-3 h-3" /> Preview</>}
                        </button>
                    </div>
                    {preview[key] ? (
                        <div className="border rounded-lg p-3 bg-gray-50 min-h-[80px]">
                            <MarkdownPreview content={form[key] || ''} />
                        </div>
                    ) : (
                        <textarea value={form[key] || ''} onChange={(e) => setForm({ ...form, [key]: e.target.value })}
                            className="w-full border rounded-lg px-3 py-2 h-40" placeholder={placeholder} />
                    )}
                </div>
            ))}
        </div>
    );
}

// ─── LLM Tab ──────────────────────────────────────────────────────
function LLMTab({ form, setForm, agentId }) {
    const providers = ['ollama', 'openrouter', 'openai', 'anthropic', 'custom'];
    const [testing, setTesting] = useState(false);
    const [testResult, setTestResult] = useState(null);

    const testLLM = async () => {
        setTesting(true);
        setTestResult(null);
        try {
            const params = new URLSearchParams();
            if (form.llm_provider) params.set('provider', form.llm_provider);
            if (form.llm_model) params.set('model', form.llm_model);
            if (form.llm_base_url) params.set('base_url', form.llm_base_url);
            params.set('temperature', '0.1');
            params.set('max_tokens', '50');
            const res = await api.post(`/${agentId}/test-llm?${params.toString()}`);
            setTestResult(res.data);
            toast.success(`LLM OK — ${res.data.latency_ms}ms, "${res.data.response_preview}"`);
        } catch (e) {
            const detail = e.response?.data?.detail || e.message;
            setTestResult({ status: 'error', error: detail });
            toast.error(`LLM test failed: ${detail}`);
        } finally {
            setTesting(false);
        }
    };

    return (
        <div className="space-y-5">
            <div className="grid grid-cols-2 gap-4">
                <div>
                    <label className="block text-sm font-medium mb-1">Provider</label>
                    <select value={form.llm_provider || 'ollama'} onChange={(e) => setForm({ ...form, llm_provider: e.target.value })}
                        className="w-full border rounded-lg px-3 py-2">
                        {providers.map((p) => <option key={p} value={p}>{p}</option>)}
                    </select>
                </div>
                <div>
                    <label className="block text-sm font-medium mb-1">Model</label>
                    <input type="text" value={form.llm_model || ''} onChange={(e) => setForm({ ...form, llm_model: e.target.value })}
                        className="w-full border rounded-lg px-3 py-2" placeholder="qwen3.5:4b" />
                </div>
            </div>
            <div>
                <label className="block text-sm font-medium mb-1">
                    Temperature: <span className="font-mono">{form.llm_temperature ?? 0.7}</span>
                </label>
                <input type="range" min="0" max="2" step="0.1" value={form.llm_temperature ?? 0.7}
                    onChange={(e) => setForm({ ...form, llm_temperature: parseFloat(e.target.value) })}
                    className="w-full" />
                <div className="flex justify-between text-xs text-gray-400 mt-1">
                    <span>Precise (0)</span><span>Balanced (1)</span><span>Creative (2)</span>
                </div>
            </div>
            <div className="grid grid-cols-2 gap-4">
                <div>
                    <label className="block text-sm font-medium mb-1">Max Tokens</label>
                    <input type="number" value={form.llm_max_tokens || 4096}
                        onChange={(e) => setForm({ ...form, llm_max_tokens: parseInt(e.target.value) || 4096 })}
                        className="w-full border rounded-lg px-3 py-2" />
                </div>
                <div>
                    <label className="block text-sm font-medium mb-1">Base URL <span className="text-gray-400">(optional)</span></label>
                    <input type="text" value={form.llm_base_url || ''} onChange={(e) => setForm({ ...form, llm_base_url: e.target.value })}
                        className="w-full border rounded-lg px-3 py-2" placeholder="http://localhost:11434" />
                </div>
            </div>

            {/* Test LLM Connection */}
            <div className="border-t pt-5">
                <div className="flex items-center justify-between mb-3">
                    <div>
                        <h3 className="text-sm font-semibold">Test LLM Connection</h3>
                        <p className="text-xs text-gray-400 mt-0.5">
                            Sends a minimal chat request to verify the configured LLM is reachable.
                        </p>
                    </div>
                    <button onClick={testLLM} disabled={testing}
                        className={`px-4 py-2 rounded-lg flex items-center gap-2 text-sm font-medium ${
                            testing
                                ? 'bg-gray-200 text-gray-500 cursor-wait'
                                : 'bg-emerald-600 hover:bg-emerald-700 text-white'
                        }`}>
                        {testing ? (
                            <><RefreshCw className="w-4 h-4 animate-spin" /> Testing...</>
                        ) : (
                            <><Cpu className="w-4 h-4" /> Test Connection</>
                        )}
                    </button>
                </div>

                {testResult && (
                    <div className={`rounded-lg p-4 text-sm ${
                        testResult.status === 'ok'
                            ? 'bg-green-50 border border-green-200 text-green-800'
                            : 'bg-red-50 border border-red-200 text-red-800'
                    }`}>
                        {testResult.status === 'ok' ? (
                            <div className="space-y-1">
                                <div className="flex items-center gap-2 font-medium">
                                    <span className="text-green-600">✓</span>
                                    Connection successful
                                </div>
                                <div className="grid grid-cols-2 gap-x-4 gap-y-1 mt-2 text-xs">
                                    <span className="text-gray-500">Provider:</span>
                                    <span className="font-mono">{testResult.provider}</span>
                                    <span className="text-gray-500">Model:</span>
                                    <span className="font-mono">{testResult.model}</span>
                                    <span className="text-gray-500">Base URL:</span>
                                    <span className="font-mono">{testResult.base_url}</span>
                                    <span className="text-gray-500">Latency:</span>
                                    <span className="font-mono">{testResult.latency_ms}ms</span>
                                    {testResult.usage && (
                                        <>
                                            <span className="text-gray-500">Tokens:</span>
                                            <span className="font-mono">
                                                {testResult.usage.prompt_tokens}+{testResult.usage.completion_tokens}={testResult.usage.total_tokens}
                                            </span>
                                        </>
                                    )}
                                </div>
                                <div className="mt-2 pt-2 border-t border-green-200">
                                    <span className="text-gray-500 text-xs">Response: </span>
                                    <span className="font-mono text-xs">&ldquo;{testResult.response_preview}&rdquo;</span>
                                </div>
                            </div>
                        ) : (
                            <div className="flex items-start gap-2">
                                <span className="text-red-600 mt-0.5">✗</span>
                                <span className="break-all">{testResult.error}</span>
                            </div>
                        )}
                    </div>
                )}
            </div>
        </div>
    );
}

// ─── Skills Tab ───────────────────────────────────────────────────
function SkillsTab({ form, setForm }) {
    const [installUrl, setInstallUrl] = useState('');
    const [installing, setInstalling] = useState(false);
    const skillsList = form.skills_config || [];

    const toggleSkill = (skillName) => {
        setForm((prev) => ({
            ...prev,
            skills_config: prev.skills_config.map((s) =>
                s.name === skillName ? { ...s, enabled: !s.enabled } : s
            ),
        }));
    };

    const installPlugin = async () => {
        if (!installUrl.trim()) return;
        setInstalling(true);
        try {
            const res = await api.post(`/${form.id}/install-skill`, { source_url: installUrl.trim() });
            toast.success(res.data.message || 'Plugin installed');
            if (res.data.skills_config) setForm((prev) => ({ ...prev, skills_config: res.data.skills_config }));
            setInstallUrl('');
        } catch (e) {
            toast.error(e.response?.data?.detail || 'Failed to install plugin');
        } finally {
            setInstalling(false);
        }
    };

    const removePlugin = async (skillId) => {
        if (!window.confirm('Remove this plugin?')) return;
        try {
            const res = await api.delete(`/${form.id}/skills/${skillId}`);
            toast.success('Plugin removed');
            setForm((prev) => ({ ...prev, skills_config: res.data.skills_config || prev.skills_config.filter((s) => s.id !== skillId) }));
        } catch (e) {
            toast.error(e.response?.data?.detail || 'Failed to remove plugin');
        }
    };

    const nativeSkills = skillsList.filter((s) => s.type !== 'plugin');
    const plugins = skillsList.filter((s) => s.type === 'plugin');

    return (
        <div className="space-y-6">
            <div>
                <h3 className="text-sm font-semibold mb-3">Native Skills</h3>
                <div className="divide-y border rounded-lg">
                    {nativeSkills.map((skill) => (
                        <div key={skill.name} className="flex items-center justify-between px-4 py-3">
                            <div>
                                <span className="text-sm font-medium">{skill.name}</span>
                                {skill.name.startsWith('task.') && <span className="ml-2 text-xs text-gray-400">Task</span>}
                                {skill.name.startsWith('channel.') && <span className="ml-2 text-xs text-gray-400">Channel</span>}
                            </div>
                            <button onClick={() => toggleSkill(skill.name)}
                                className={`relative w-11 h-6 rounded-full transition-colors ${skill.enabled ? 'bg-blue-600' : 'bg-gray-300'}`}>
                                <span className={`absolute top-0.5 left-0.5 w-5 h-5 rounded-full bg-white shadow transition-transform ${skill.enabled ? 'translate-x-5' : ''}`} />
                            </button>
                        </div>
                    ))}
                    {nativeSkills.length === 0 && <div className="px-4 py-3 text-sm text-gray-400">No native skills configured</div>}
                </div>
            </div>

            {plugins.length > 0 && (
                <div>
                    <h3 className="text-sm font-semibold mb-3">Installed Plugins</h3>
                    <div className="divide-y border rounded-lg">
                        {plugins.map((p) => (
                            <div key={p.id} className="flex items-center justify-between px-4 py-3">
                                <div>
                                    <span className="text-sm font-medium">{p.name}</span>
                                    {p.version && <span className="ml-2 text-xs text-gray-400">v{p.version}</span>}
                                    {p.source_url && <a href={p.source_url} target="_blank" rel="noopener" className="ml-1 text-xs text-blue-500"><Github className="w-3 h-3 inline" /></a>}
                                </div>
                                <div className="flex items-center gap-3">
                                    <button onClick={() => toggleSkill(p.name)}
                                        className={`relative w-11 h-6 rounded-full transition-colors ${p.enabled ? 'bg-blue-600' : 'bg-gray-300'}`}>
                                        <span className={`absolute top-0.5 left-0.5 w-5 h-5 rounded-full bg-white shadow transition-transform ${p.enabled ? 'translate-x-5' : ''}`} />
                                    </button>
                                    <button onClick={() => removePlugin(p.id)} className="text-red-500 hover:text-red-700"><Trash2 className="w-4 h-4" /></button>
                                </div>
                            </div>
                        ))}
                    </div>
                </div>
            )}

            <div>
                <h3 className="text-sm font-semibold mb-3">Add Plugin from GitHub</h3>
                <div className="flex gap-2">
                    <input type="text" value={installUrl} onChange={(e) => setInstallUrl(e.target.value)}
                        className="flex-1 border rounded-lg px-3 py-2" placeholder="https://github.com/user/plugin-repo"
                        onKeyDown={(e) => e.key === 'Enter' && installPlugin()} />
                    <button onClick={installPlugin} disabled={installing || !installUrl.trim()}
                        className="bg-blue-600 hover:bg-blue-700 disabled:bg-gray-300 text-white px-4 py-2 rounded-lg flex items-center gap-1">
                        {installing ? <RefreshCw className="w-4 h-4 animate-spin" /> : <Plus className="w-4 h-4" />}
                        Install
                    </button>
                </div>
            </div>
        </div>
    );
}

// ─── Memory Tab ───────────────────────────────────────────────────
function MemoryTab({ agentId }) {
    const [memories, setMemories] = useState([]);
    const [loading, setLoading] = useState(true);

    useEffect(() => {
        loadMemory();
    }, [agentId]);

    async function loadMemory() {
        setLoading(true);
        try {
            const res = await api.get(`/${agentId}/memory`);
            setMemories(res.data || []);
        } catch {
            setMemories([]);
        } finally {
            setLoading(false);
        }
    }

    if (loading) return <div className="text-gray-500">Loading memory...</div>;
    if (memories.length === 0) return (
        <div className="text-center py-12">
            <Brain className="w-12 h-12 text-gray-300 mx-auto mb-3" />
            <p className="text-gray-500">No space memory yet.</p>
            <p className="text-sm text-gray-400 mt-1">Memory will be created automatically as the agent processes tasks in workspaces.</p>
        </div>
    );

    return (
        <div className="space-y-4">
            {memories.map((mem) => (
                <div key={mem.workspace_id} className="border rounded-lg p-4">
                    <div className="flex items-center justify-between mb-2">
                        <h4 className="font-medium">{mem.workspace_name || mem.workspace_id}</h4>
                        <span className="text-xs text-gray-400">{mem.updated_at ? new Date(mem.updated_at).toLocaleDateString() : ''}</span>
                    </div>
                    {mem.facts?.length > 0 && (
                        <div className="mb-2">
                            <span className="text-xs font-medium text-gray-500">Facts:</span>
                            <ul className="ml-4 text-sm">{mem.facts.map((f, i) => <li key={i} className="list-disc">{f}</li>)}</ul>
                        </div>
                    )}
                    {mem.repo_urls?.length > 0 && (
                        <div className="mb-2">
                            <span className="text-xs font-medium text-gray-500">Repos:</span>
                            <ul className="ml-4 text-sm">{mem.repo_urls.map((u, i) => <li key={i}><a href={u} className="text-blue-500">{u}</a></li>)}</ul>
                        </div>
                    )}
                    {mem.conventions?.length > 0 && (
                        <div><span className="text-xs font-medium text-gray-500">Conventions:</span>
                            <ul className="ml-4 text-sm">{mem.conventions.map((c, i) => <li key={i} className="list-disc">{c}</li>)}</ul>
                        </div>
                    )}
                </div>
            ))}
        </div>
    );
}

// ─── Env Vars Tab ─────────────────────────────────────────────────
function EnvVarsTab({ form, setForm }) {
    const envVars = form.env_vars || {};
    const [newKey, setNewKey] = useState('');
    const [newValueType, setNewValueType] = useState('plain');
    const [showValues, setShowValues] = useState({});

    const addVar = () => {
        if (!newKey.trim()) return;
        setForm((prev) => ({
            ...prev,
            env_vars: { ...prev.env_vars, [newKey.trim()]: valueType === 'secret' ? '' : '' },
        }));
        setNewKey('');
    };

    const updateVar = (key, value) => {
        setForm((prev) => ({
            ...prev,
            env_vars: { ...prev.env_vars, [key]: value },
        }));
    };

    const removeVar = (key) => {
        setForm((prev) => {
            const { [key]: _, ...rest } = prev.env_vars;
            return { ...prev, env_vars: rest };
        });
    };

    const toggleShowValue = (key) => setShowValues((p) => ({ ...p, [key]: !p[key] }));

    const entries = Object.entries(envVars);

    return (
        <div className="space-y-4">
            <div className="bg-yellow-50 border border-yellow-200 rounded-lg p-3 text-sm text-yellow-800">
                <strong>Warning:</strong> Secret values are never shown after save. Leave a secret value empty to keep the existing value.
            </div>

            {entries.length > 0 && (
                <div className="border rounded-lg overflow-hidden">
                    <div className="grid grid-cols-12 gap-2 px-4 py-2 bg-gray-50 text-xs font-medium text-gray-500">
                        <div className="col-span-3">Key</div>
                        <div className="col-span-6">Value</div>
                        <div className="col-span-2">Type</div>
                        <div className="col-span-1"></div>
                    </div>
                    {entries.map(([key, value]) => {
                        const isSecret = key.toUpperCase().includes('TOKEN') || key.toUpperCase().includes('KEY') || key.toUpperCase().includes('SECRET') || key.toUpperCase().includes('PASSWORD');
                        const masked = isSecret && !showValues[key];
                        return (
                            <div key={key} className="grid grid-cols-12 gap-2 px-4 py-2 border-t items-center">
                                <div className="col-span-3 font-mono text-sm">{key}</div>
                                <div className="col-span-6 flex items-center gap-1">
                                    {isSecret ? (
                                        <div className="flex-1 flex items-center gap-1">
                                            <input type={showValues[key] ? 'text' : 'password'} value={value || ''}
                                                onChange={(e) => updateVar(key, e.target.value)}
                                                className="flex-1 border rounded px-2 py-1 text-sm font-mono" placeholder="Enter new value..." />
                                            <button onClick={() => toggleShowValue(key)} className="text-gray-400 hover:text-gray-600">
                                                {showValues[key] ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
                                            </button>
                                        </div>
                                    ) : (
                                        <input type="text" value={value || ''} onChange={(e) => updateVar(key, e.target.value)}
                                            className="w-full border rounded px-2 py-1 text-sm font-mono" />
                                    )}
                                </div>
                                <div className="col-span-2">
                                    <span className={`text-xs px-2 py-1 rounded ${isSecret ? 'bg-red-100 text-red-700' : 'bg-green-100 text-green-700'}`}>
                                        {isSecret ? 'secret' : 'plain'}
                                    </span>
                                </div>
                                <div className="col-span-1">
                                    <button onClick={() => removeVar(key)} className="text-red-400 hover:text-red-600"><Trash2 className="w-4 h-4" /></button>
                                </div>
                            </div>
                        );
                    })}
                </div>
            )}

            <div className="flex gap-2 items-end">
                <input type="text" value={newKey} onChange={(e) => setNewKey(e.target.value)}
                    className="flex-1 border rounded-lg px-3 py-2 text-sm font-mono" placeholder="NEW_VAR_NAME"
                    onKeyDown={(e) => e.key === 'Enter' && addVar()} />
                <button onClick={addVar} disabled={!newKey.trim()}
                    className="bg-blue-600 hover:bg-blue-700 disabled:bg-gray-300 text-white px-4 py-2 rounded-lg flex items-center gap-1">
                    <Plus className="w-4 h-4" /> Add
                </button>
            </div>
        </div>
    );
}

// ─── Main Component ───────────────────────────────────────────────
export default function AgentConfigPanel() {
    const { agentId } = useParams();
    const navigate = useNavigate();
    const [activeTab, setActiveTab] = useState('general');
    const [agent, setAgent] = useState(null);
    const [form, setForm] = useState({});
    const [originalForm, setOriginalForm] = useState({});
    const [loading, setLoading] = useState(true);
    const [saving, setSaving] = useState(false);
    const [lastRefresh, setLastRefresh] = useState(null);
    const [workspaces, setWorkspaces] = useState([]);

    // Load agent data
    const loadAgent = useCallback(async () => {
        try {
            const res = await api.get(`/${agentId}`);
            const data = res.data;
            setAgent(data);
            const formData = {
                ...data,
                skills_config: data.skills_config || [],
                workspace_ids: data.workspace_ids || [],
                env_vars: data.env_vars || {},
                max_concurrent_tasks: data.max_concurrent_tasks || 3,
                llm_temperature: data.llm_temperature ?? 0.7,
                llm_max_tokens: data.llm_max_tokens || 4096,
            };
            setForm(formData);
            setOriginalForm(JSON.parse(JSON.stringify(formData)));
            setLastRefresh(Date.now());
        } catch (e) {
            toast.error('Failed to load agent');
            console.error(e);
        } finally {
            setLoading(false);
        }
    }, [agentId]);

    // Load workspaces
    useEffect(() => {
        async function loadWorkspaces() {
            try {
                const res = await axios.get(`${API_BASE}/api/superadmin/workspaces`, {
                    headers: { Authorization: `Bearer ${localStorage.getItem('superadmin_token')}` },
                });
                setWorkspaces(res.data || []);
            } catch (e) {
                console.error('Failed to load workspaces:', e);
            }
        }
        loadWorkspaces();
    }, []);

    useEffect(() => { loadAgent(); }, [loadAgent]);

    // Auto-refresh every 30s (passive)
    useEffect(() => {
        const interval = setInterval(() => {
            loadAgent();
        }, 30000);
        return () => clearInterval(interval);
    }, [loadAgent]);

    // Dirty check
    const isDirty = JSON.stringify(form) !== JSON.stringify(originalForm);

    // Save
    async function save() {
        setSaving(true);
        try {
            await api.put(`/${agentId}`, form);
            toast.success('Agent configuration saved');
            setOriginalForm(JSON.parse(JSON.stringify(form)));
            setLastRefresh(Date.now());
        } catch (e) {
            toast.error(e.response?.data?.detail || 'Failed to save');
        } finally {
            setSaving(false);
        }
    }

    // Last updated text
    const lastUpdatedText = lastRefresh
        ? `Last updated ${Math.round((Date.now() - lastRefresh) / 1000)}s ago`
        : '';

    if (loading) return <div className="p-6 text-gray-500">Loading agent configuration...</div>;
    if (!agent) return <div className="p-6 text-gray-500">Agent not found.</div>;

    const statusClass = statusColors[agent.status] || 'bg-gray-100 text-gray-800';

    return (
        <div className="p-6 max-w-5xl mx-auto">
            {/* Header */}
            <div className="flex items-center justify-between mb-4">
                <div className="flex items-center gap-3">
                    <button onClick={() => navigate('/superadmin/ai-agents')} className="p-2 hover:bg-gray-100 rounded-lg">
                        <ArrowLeft className="w-5 h-5" />
                    </button>
                    <div>
                        <h1 className="text-xl font-bold flex items-center gap-2">
                            <Bot className="w-5 h-5" /> {agent.name}
                        </h1>
                        <div className="text-xs text-gray-400 mt-0.5">{lastUpdatedText}</div>
                    </div>
                    <span className={`text-xs px-2 py-1 rounded-full ${statusClass}`}>{agent.status}</span>
                </div>
                <div className="flex items-center gap-2">
                    {isDirty && <span className="text-sm text-orange-600">Unsaved changes</span>}
                    <button onClick={save} disabled={saving || !isDirty}
                        className={`px-4 py-2 rounded-lg flex items-center gap-2 ${saving || !isDirty ? 'bg-gray-300 text-gray-500' : 'bg-blue-600 hover:bg-blue-700 text-white'}`}>
                        <Save className="w-4 h-4" /> {saving ? 'Saving...' : 'Save'}
                    </button>
                </div>
            </div>

            {/* Tabs */}
            <div className="border-b mb-6">
                <nav className="flex gap-1">
                    {TABS.map(({ key, label, icon: Icon }) => (
                        <button key={key} onClick={() => setActiveTab(key)}
                            className={`px-4 py-2 text-sm font-medium border-b-2 transition-colors flex items-center gap-1.5 ${
                                activeTab === key ? 'border-blue-600 text-blue-600' : 'border-transparent text-gray-500 hover:text-gray-700'
                            }`}>
                            <Icon className="w-4 h-4" /> {label}
                        </button>
                    ))}
                </nav>
            </div>

            {/* Tab Content */}
            <div className="bg-white rounded-lg shadow p-6">
                {activeTab === 'general' && <GeneralTab form={form} setForm={setForm} workspaces={workspaces} />}
                {activeTab === 'persona' && <PersonaTab form={form} setForm={setForm} />}
                {activeTab === 'llm' && <LLMTab form={form} setForm={setForm} agentId={agentId} />}
                {activeTab === 'skills' && <SkillsTab form={form} setForm={setForm} />}
                {activeTab === 'memory' && <MemoryTab agentId={agentId} />}
                {activeTab === 'vars' && <EnvVarsTab form={form} setForm={setForm} />}
            </div>
        </div>
    );
}

export { AgentConfigPanel };
export const AgentConfig = AgentConfigPanel;